import { MouseEvent } from 'react'

import {
  Notification,
  NotificationReason,
  PayloadType,
} from '@tribeplatform/gql-client/types'
import {
  useReadNotification,
  useSubscriptionState,
  useUpdateSubscription,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast, useToastContext } from '@tribeplatform/react-ui-kit/Toast'

import { getPostIdFromPath } from '../common/utils/post.js'
import { T } from '../i18n/components/T.js'
import { useI18n } from '../i18n/providers/I18nProvider.js'

export interface NotificationPublisher {
  type: 'post' | 'space'
  id: string
  name?: string
}

/** Root post id from the notification link; `target` is only the direct parent. */
const getRootPostId = (notification: Notification) => {
  const path = notification.meta?.relativeUrl?.split(/[?#]/)[0]
  return (path && getPostIdFromPath(path)) || undefined
}

/** Post or space subscription behind a notification, if any. */
export const getNotificationPublisher = (
  notification: Notification,
): NotificationPublisher | undefined => {
  const { meta, target, space, publisher } = notification

  if (publisher?.type === PayloadType.POST) {
    return { type: 'post', id: publisher.id, name: meta?.textTitle }
  }
  if (publisher?.type === PayloadType.SPACE) {
    return {
      type: 'space',
      id: publisher.id,
      name: publisher.summary || publisher.name,
    }
  }

  if (meta?.reason === NotificationReason.POST_SUBSCRIPTION) {
    const id =
      getRootPostId(notification) ||
      (target?.type === PayloadType.POST ? target.id : undefined)
    if (id) {
      return { type: 'post', id, name: meta.textTitle }
    }
    return undefined
  }

  if (meta?.reason === NotificationReason.SPACE_SUBSCRIPTION) {
    if (target?.type === PayloadType.SPACE && target.id) {
      return {
        type: 'space',
        id: target.id,
        name: target.summary || target.name,
      }
    }
    if (space?.id) {
      return { type: 'space', id: space.id, name: space.name }
    }
  }

  return undefined
}

const UnfollowedToast = ({
  publisher,
  onUndo,
}: {
  publisher: NotificationPublisher
  onUndo: () => void
}) => {
  const { $t } = useI18n()
  const { onClose } = useToastContext()

  return (
    <Alert
      status="neutral"
      withClose
      onClose={onClose}
      title={
        publisher.type === 'post'
          ? $t(
              {
                defaultMessage: "You're no longer following {post}.",
                id: 'Notifications.Unfollowed.Post',
              },
              { post: publisher.name },
            )
          : $t(
              {
                defaultMessage:
                  "You're no longer following {space}. You'll still be notified about mentions and replies to your posts.",
                id: 'Notifications.Unfollowed.Space',
              },
              { space: publisher.name },
            )
      }
    >
      <Alert.Actions>
        <Button
          size="sm"
          variant="secondaryNeutral"
          onClick={() => {
            onUndo()
            onClose()
          }}
        >
          <T defaultMessage="Undo" id="Generics.Undo" />
        </Button>
      </Alert.Actions>
    </Alert>
  )
}

/** Unfollow / Mark as read actions under a notification row. */
export const NotificationRowActions = ({
  notification,
}: {
  notification: Notification
}) => {
  const { $t } = useI18n()
  const { mutate: readNotification } = useReadNotification()
  const { mutateAsync: updateSubscription } = useUpdateSubscription()

  const publisher = getNotificationPublisher(notification)
  const sessionState = useSubscriptionState(publisher?.id)
  const unfollowed = sessionState
    ? !sessionState.subscribed
    : notification.subscribed === false
  const canMarkAsRead = !notification.read

  if (!publisher && !canMarkAsRead) {
    return null
  }

  const stop = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const setSubscribed = (subscribed: boolean) =>
    updateSubscription({
      action: subscribed ? 'subscribe' : 'unsubscribe',
      publisherType: publisher.type,
      publisherId: publisher.id,
    })

  const unfollow = async () => {
    try {
      await setSubscribed(false)
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to unfollow. Please try again.',
          id: 'Notifications.Unfollow.Failed',
        }),
        status: 'error',
      })
      return
    }
    toast({
      custom: (
        <UnfollowedToast
          publisher={publisher}
          onUndo={() => setSubscribed(true)}
        />
      ),
    })
  }

  const unfollowLabel =
    publisher?.type === 'post'
      ? $t({
          defaultMessage: 'Unfollow post',
          id: 'Notifications.Row.UnfollowPost',
        })
      : $t(
          {
            defaultMessage: 'Unfollow {space}',
            id: 'Notifications.Row.UnfollowSpace',
          },
          { space: publisher?.name },
        )

  return (
    <div className="flex flex-wrap items-center gap-3 pt-2" onClick={stop}>
      {publisher &&
        (unfollowed ? (
          <span className="text-sm text-content-disabled">
            <T defaultMessage="Unfollowed" id="Notifications.Row.Unfollowed" />
          </span>
        ) : (
          <Button
            size="sm"
            variant="tertiaryNeutral"
            truncate={false}
            leadingIcon={<SvgIcon name="bell" />}
            aria-label={unfollowLabel}
            title={unfollowLabel}
            onClick={unfollow}
          >
            <T defaultMessage="Unfollow" id="Notifications.Row.Unfollow" />
          </Button>
        ))}
      {canMarkAsRead && (
        <Button
          size="sm"
          variant="tertiaryNeutral"
          truncate={false}
          leadingIcon={<SvgIcon name="check" />}
          onClick={() => readNotification({ notificationId: notification.id })}
        >
          <T defaultMessage="Mark as read" id="Notifications.MarkAsRead" />
        </Button>
      )}
    </div>
  )
}
